import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import {
  usePayers, useNetworkPlans, useNetworkVerifications, useNetworkPlanProfiles,
  useAddNetworkPlan, useSetPlanTrusted, fetchPlanAppointments,
} from '../api/hooks';
import PageHeader from '../components/PageHeader';
import Stat from '../components/Stat';
import PlanStageStepper from '../components/PlanStageStepper';
import StatusPill from '../components/StatusPill';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import ArtifactModal from '../components/ArtifactModal';
import NetworkImport from '../components/NetworkImport';
import NetworkAddPlan from '../components/NetworkAddPlan';
import AffectedAppointmentsDrawer from '../components/AffectedAppointmentsDrawer';
import { appealEvidencePacket } from '../lib/artifacts';
import {
  Search, Plus, RefreshCw, Globe, CheckCircle2, AlertCircle, HelpCircle, ShieldAlert,
  FileText, UploadCloud, ChevronRight, ChevronDown, Phone, ShieldCheck, ShieldOff,
} from 'lucide-react';
import { toast } from '../lib/aiToast';

const FILTERS = [{ k: 'all', label: 'All' }, { k: 'in_network', label: 'In' }, { k: 'out_of_network', label: 'Out' }, { k: 'unknown', label: 'Unknown' }];

// Human labels for the provenance of an added plan.
const SOURCES = {
  csv_import: 'CSV import', phone_call: 'Phone verification', manual: 'Manual entry',
  agent: 'Agent', '271_match': 'Eligibility match', contracted: 'Contracted', '271': 'Eligibility 271',
};

const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
const money = (n) => (n == null ? '—' : `$${Math.round(n).toLocaleString()}`);
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : null);
const pct = (n) => `${Math.round((n || 0) * 100)}%`;

// Plan-risk → shared status pill (high / medium / low / clear), with the score.
function RiskPill({ risk }) {
  if (!risk) return <span className="text-slate-400 text-xs">—</span>;
  return <StatusPill status={risk.level} label={`${cap(risk.level)} · ${risk.score}`} />;
}

// The expandable "cases dealt + denials" panel under a plan row.
function PlanDrillDown({ p }) {
  const c = p.cases || {};
  const d = p.denials || {};
  const risk = p.planRisk || {};
  return (
    <div className="grid md:grid-cols-3 gap-4 px-4 py-4 bg-slate-50/60 border-t border-slate-100 text-sm">
      {/* Cases dealt */}
      <div>
        <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-2">Cases dealt</div>
        <div className="space-y-1 text-slate-700">
          <Row label="Eligibility checks" value={c.total ?? 0} />
          <Row label="Out-of-network" value={c.outOfNetwork ?? 0} warn={c.outOfNetwork > 0} />
          <Row label="Inactive coverage" value={c.inactive ?? 0} warn={c.inactive > 0} />
          <Row label="Referral / prior-auth" value={(c.referral ?? 0) + (c.priorAuth ?? 0)} />
          <Row label="Avg copay" value={money(c.avgCopay)} />
          {c.lastSeen && <Row label="Last seen" value={fmtDate(c.lastSeen)} />}
        </div>
      </div>

      {/* Denials */}
      <div>
        <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-2">
          Denials {d.deniedCount > 0 && <span className="text-rose-600">· {d.deniedCount} ({money(d.deniedAmount)})</span>}
        </div>
        {d.deniedCount > 0 || (d.patterns || []).length > 0 ? (
          <div className="space-y-1.5">
            {(d.byReason || []).map((r, i) => (
              <div key={i} className="flex items-center justify-between gap-2">
                <span className="text-slate-700 truncate" title={r.reason}>{r.code ? `${r.code} · ` : ''}{r.reason}</span>
                <span className="text-rose-600 font-medium tnum whitespace-nowrap">{r.count}× · {money(r.amount)}</span>
              </div>
            ))}
            {(d.patterns || []).map((pt, i) => (
              <div key={`pt${i}`} className="text-[12px] text-slate-500 border-l-2 border-amber-300 pl-2">
                {pt.cpt ? `${pt.cpt} · ` : ''}{pt.reason}
                {pt.recommendedAction && <div className="text-slate-600 mt-0.5">→ {pt.recommendedAction}</div>}
              </div>
            ))}
            {d.claims > 0 && <div className="text-[11px] text-slate-400 pt-0.5">{d.deniedCount}/{d.claims} claims denied · {pct(d.denialRate)}</div>}
          </div>
        ) : (
          <div className="text-slate-400 text-xs">No denials on record for this plan.</div>
        )}
      </div>

      {/* Plan risk + trust */}
      <div>
        <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-2">
          Plan risk · {risk.score} <RiskPill risk={risk} />
        </div>
        {(risk.factors || []).length ? (
          <div className="space-y-1">
            {risk.factors.map((f, i) => (
              <div key={i} className="flex items-start justify-between gap-2">
                <span className="text-slate-700">{f.label}<span className="block text-[10px] text-slate-400">{f.source}</span></span>
                <span className="text-slate-500 font-medium tnum">+{f.points}</span>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-slate-400 text-xs">No risk factors — clean plan.</div>
        )}
        {p.stageInfo?.detail && (
          <div className="mt-3 text-[12px] text-slate-600 flex items-start gap-1.5">
            <ShieldCheck className="h-3.5 w-3.5 text-slate-400 mt-0.5 shrink-0" />
            {p.stageInfo.detail}
          </div>
        )}
      </div>
    </div>
  );
}

const Row = ({ label, value, warn }) => (
  <div className="flex items-center justify-between gap-2">
    <span className="text-slate-500">{label}</span>
    <span className={`font-medium tnum ${warn ? 'text-rose-600' : 'text-slate-800'}`}>{value}</span>
  </div>
);

export default function Network() {
  const { session } = useAuth();
  const navigate = useNavigate();
  const practiceId = session?.practice?.id || 'prac_001';
  const isAdmin = session?.user?.role === 'admin';
  const { data: payersAll = [] } = usePayers();
  const { data: profiles = [] } = useNetworkPlanProfiles(practiceId);
  const { data: addedPlans = [] } = useNetworkPlans(practiceId);
  const { data: verifications = [] } = useNetworkVerifications(practiceId);
  const addPlan = useAddNetworkPlan();
  const setPlanTrusted = useSetPlanTrusted();
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState('all');
  const [payer, setPayer] = useState('all');
  const [overrides, setOverrides] = useState({});
  const [evidence, setEvidence] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [expanded, setExpanded] = useState(() => new Set());
  const [affected, setAffected] = useState(null);
  const [busyId, setBusyId] = useState(null);

  // Provenance (addedBy / addedAt / evidence) lives on the added-plans record;
  // the profile carries everything else. Merge by plan_key for the evidence packet.
  const provByKey = useMemo(() => {
    const m = new Map();
    addedPlans.forEach((p) => m.set(p.planKey, p));
    return m;
  }, [addedPlans]);

  const rows = useMemo(() => profiles.map((p) => {
    const prov = provByKey.get(p.planKey);
    return {
      ...p,
      id: p.planKey,
      baseStatus: p.status,
      provenance: prov ? { addedBy: prov.addedBy, addedAt: prov.lastVerifiedAt, lastVerifiedAt: prov.lastVerifiedAt, evidence: prov.evidence } : null,
    };
  }), [profiles, provByKey]);

  const withStatus = rows.map((r) => ({ ...r, status: overrides[r.id] || r.baseStatus }));

  const filtered = withStatus.filter((r) => {
    if (filter !== 'all') {
      if (filter === 'unknown' ? !(r.status === 'unknown' || r.status === 'not_in_db') : r.status !== filter) return false;
    }
    if (payer !== 'all' && r.payerId !== payer) return false;
    if (!q) return true;
    return `${r.payerName} ${r.planName}`.toLowerCase().includes(q.toLowerCase());
  });

  const payers = useMemo(() => Array.from(new Map(rows.map((r) => [r.payerId, payersAll.find((p) => p.id === r.payerId)]).filter(([id, p]) => id && p)).values()), [rows, payersAll]);

  // Persist the new status to the network library, re-derive the worklist, and
  // open the drawer with every affected appointment's before → after risk.
  const applyStatus = async (row, status) => {
    if (status === row.status || busyId) return;
    const fromStatus = row.status;
    const prevOverride = overrides[row.id];
    setBusyId(row.id);
    setOverrides((o) => ({ ...o, [row.id]: status })); // optimistic table update
    try {
      const before = await fetchPlanAppointments({ practiceId, planKey: row.planKey });
      // payerId is required to persist; observed-only plans without one preview instead.
      if (row.payerId) {
        await addPlan.mutateAsync({
          practiceId, payerId: row.payerId, planName: row.planName,
          planType: row.planType || undefined, status, source: 'manual',
        });
      }
      const after = row.payerId
        ? await fetchPlanAppointments({ practiceId, planKey: row.planKey })
        : before;
      const beforeById = new Map(before.map((a) => [a.id, a]));
      const rows = after.map((a) => ({ ...a, before: beforeById.get(a.id)?.risk || null }));
      const changed = rows.filter((a) => a.before && a.before.level !== a.risk?.level).length;
      setAffected({ plan: row, fromStatus, toStatus: status, rows, changed });
      toast.success(`Network status updated · ${rows.length} appointment${rows.length === 1 ? '' : 's'} affected`);
    } catch (e) {
      setOverrides((o) => ({ ...o, [row.id]: prevOverride })); // revert optimism
      toast.error('Could not update network status');
    } finally {
      setBusyId(null);
    }
  };
  // Admin-only trust override: vouch for a plan so it bypasses the verification
  // gate (one phone confirmation + N clean visits) and is treated as established.
  const applyTrusted = async (row, trusted) => {
    if (busyId) return;
    setBusyId(row.id);
    try {
      await setPlanTrusted.mutateAsync({
        practiceId, planKey: row.planKey, payerId: row.payerId || undefined,
        planName: row.planName, trusted,
      });
      toast.success(trusted ? `${row.planName} marked trusted` : `Trust revoked for ${row.planName}`);
    } catch (e) {
      toast.error('Could not update trust');
    } finally {
      setBusyId(null);
    }
  };
  const toggle = (id) => setExpanded((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const counts = {
    in: withStatus.filter((r) => r.status === 'in_network').length,
    out: withStatus.filter((r) => r.status === 'out_of_network').length,
    unk: withStatus.filter((r) => r.status === 'unknown' || r.status === 'not_in_db').length,
    atRisk: withStatus.filter((r) => ['high', 'medium'].includes(r.planRisk?.level)).length,
    building: withStatus.filter((r) => r.stage && r.stage !== 'established').length,
  };

  return (
    <div className="space-y-5">
      <PageHeader eyebrow="Directory" title="Plan-level network" subtitle="Every plan you deal with — the cases handled, the denials seen, where it sits in the trust lifecycle, and the risk it carries into each appointment.">
        <Button variant="outline" onClick={() => setImportOpen(true)}><UploadCloud className="h-4 w-4 mr-1.5" />Import CSV</Button>
        <Button variant="outline"><Globe className="h-4 w-4 mr-1.5" />Scrape payer directory</Button>
        <Button className="bg-brand-600 hover:bg-brand-700 text-white" onClick={() => setAddOpen(true)}><Plus className="h-4 w-4 mr-1.5" />Add plan</Button>
      </PageHeader>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="In-network plans" value={counts.in} icon={CheckCircle2} tone="emerald" />
        <Stat label="Out-of-network" value={counts.out} icon={AlertCircle} tone="rose" />
        <Stat label="Unknown / unverified" value={counts.unk} sub="Resolve to reduce risk" icon={HelpCircle} tone="amber" />
        <Stat label="Plans at risk" value={counts.atRisk} sub={`${counts.building} still earning trust`} icon={ShieldAlert} tone="rose" />
      </div>

      <div className="flex flex-wrap gap-3 items-center justify-between">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input className="pl-9 bg-white" placeholder="Search payer or plan…" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs text-slate-600" title="A new plan needs one phone confirmation plus this many clean completed visits before it's trusted and stops routing for calls">
            <ShieldCheck className="h-3.5 w-3.5 text-slate-400" />
            <span className="hidden sm:inline">Trust gate</span>
            <span className="font-semibold tnum text-slate-800">1 call + 5 visits</span>
          </div>
          <Select value={payer} onValueChange={setPayer}><SelectTrigger className="h-9 w-auto min-w-[120px] bg-white text-sm"><SelectValue placeholder="Payer" /></SelectTrigger><SelectContent><SelectItem value="all">All payers</SelectItem>{payers.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent></Select>
          <div className="seg">{FILTERS.map((f) => <button key={f.k} className={filter === f.k ? 'active' : ''} onClick={() => setFilter(f.k)}>{f.label}</button>)}</div>
          <Button variant="ghost" size="sm"><RefreshCw className="h-3.5 w-3.5 mr-1.5" />Refresh</Button>
        </div>
      </div>

      <div className="dh-card-flush overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-xs text-slate-500 bg-slate-50/70">
            <tr>{['', 'Payer', 'Plan', 'Type', 'Status', 'Plan risk', 'Trust stage', 'Source', ''].map((h, i) => <th key={i} className={`font-medium px-4 py-2.5 ${i === 8 ? 'text-right' : 'text-left'}`}>{h}</th>)}</tr>
          </thead>
          <tbody>
            {filtered.map((r) => {
              const open = expanded.has(r.id);
              return (
                <React.Fragment key={r.id}>
                  <tr className="border-t border-slate-100 row-hover cursor-pointer" onClick={() => toggle(r.id)}>
                    <td className="px-4 py-3 text-slate-400 w-6">{open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}</td>
                    <td className="px-4 py-3 text-slate-900 font-medium">{r.payerName}</td>
                    <td className="px-4 py-3 text-slate-700">{r.planName}</td>
                    <td className="px-4 py-3 text-slate-700">{r.planType || '—'}</td>
                    <td className="px-4 py-3"><StatusPill status={r.status} /></td>
                    <td className="px-4 py-3"><RiskPill risk={r.planRisk} /></td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <PlanStageStepper stage={r.stage} info={r.stageInfo} />
                        {r.trusted && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700 ring-1 ring-emerald-200" title={r.stageInfo?.detail || 'Marked trusted by an admin'}>
                            <ShieldCheck className="h-3 w-3" />Trusted
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-600 capitalize">{SOURCES[r.source] || r.source?.replace('_', ' ')}</td>
                    <td className="px-4 py-3 text-right space-x-1 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                      {r.origin === 'added' && (
                        <Button size="sm" variant="ghost" onClick={() => setEvidence(r)} className="text-xs h-7" title="Package the verification history as proof for an appeal">
                          <FileText className="h-3.5 w-3.5 mr-1" />Evidence
                        </Button>
                      )}
                      {r.stage && r.stage !== 'established' && r.origin !== 'contracted' && (
                        <span className="inline-flex items-center gap-1 text-[11px] text-amber-700"><Phone className="h-3 w-3" />Confirm</span>
                      )}
                      {isAdmin && r.origin !== 'contracted' && (
                        r.trusted ? (
                          <Button size="sm" variant="ghost" onClick={() => applyTrusted(r, false)} disabled={busyId === r.id} className="text-xs h-7 text-slate-500" title="Revoke admin trust — the plan returns to the verification lifecycle">
                            <ShieldOff className="h-3.5 w-3.5 mr-1" />Untrust
                          </Button>
                        ) : (
                          <Button size="sm" variant="ghost" onClick={() => applyTrusted(r, true)} disabled={busyId === r.id} className="text-xs h-7 text-emerald-700" title="Mark trusted — bypass the verification gate and treat as established">
                            <ShieldCheck className="h-3.5 w-3.5 mr-1" />Trust
                          </Button>
                        )
                      )}
                      {['in_network', 'out_of_network'].filter((s) => s !== r.status).map((s) => (
                        <Button key={s} size="sm" variant="ghost" onClick={() => applyStatus(r, s)} disabled={busyId === r.id} className="text-xs h-7">
                          {s === 'in_network' ? 'In' : 'Out'}
                        </Button>
                      ))}
                    </td>
                  </tr>
                  {open && (
                    <tr><td colSpan={9} className="p-0"><PlanDrillDown p={r} /></td></tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      <ArtifactModal
        open={!!evidence}
        onClose={() => setEvidence(null)}
        title="Network verification — appeal evidence"
        agent="network"
        submitLabel="Mark submitted"
        onSubmit={() => toast.success('Appeal evidence packaged')}
        generate={() => (evidence ? appealEvidencePacket({
          plan: { planName: evidence.planName, status: evidence.status, addedAt: evidence.provenance?.addedAt, addedBy: evidence.provenance?.addedBy, source: evidence.source },
          payerName: evidence.payerName,
          verifications: verifications.filter((v) => v.planKey === evidence.planKey),
          confidence: evidence.confidence,
        }) : '')}
      />

      <NetworkImport open={importOpen} onClose={() => setImportOpen(false)} practiceId={practiceId} />
      <NetworkAddPlan open={addOpen} onClose={() => setAddOpen(false)} practiceId={practiceId} />

      <AffectedAppointmentsDrawer
        data={affected}
        onClose={() => setAffected(null)}
        onApprove={() => {
          const n = affected?.rows?.length || 0;
          setAffected(null);
          toast.success(`Network change confirmed · ${n} appointment${n === 1 ? '' : 's'} re-scored`);
        }}
        onOpenWorklist={() => { setAffected(null); navigate('/app'); }}
      />
    </div>
  );
}
